"use client";

/**
 * Shared "+ Create field" action for any dropdown listing fields.
 *
 * Extracted from the retired FieldSelect: only the create flow (open state +
 * hosting a create-mode FieldEditPanel) is actually common across call sites —
 * how each site fetches and shapes its field option list varies too much to
 * share (mixed static+custom options, per-row exclusions, grouped options,
 * multi-select). Callers keep their own `useFields` call and `Combobox`/
 * `MultiCombobox` options; this hook only wires the create action on top.
 *
 * Shared layer (consumed by features/recorder, features/automations,
 * features/chat, features/dashboards, features/explore, features/folder) so
 * it must not import from any feature — eslint-plugin-boundaries blocks
 * shared → feature imports.
 */

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import {
  FieldEditPanel,
  type FieldEditPanelField,
} from "@/components/shared/fields/field-edit-panel";

interface UseFieldCreateActionOptions {
  /** Called with the newly created field (e.g. to auto-select it). */
  onCreated: (field: FieldEditPanelField) => void;
}

interface FieldCreateAction {
  /** Shape-compatible with both Combobox.topAction and MultiCombobox.topAction. */
  topAction: {
    label: ReactNode;
    onSelect: () => void;
  };
  /** Render next to the dropdown that uses `topAction` — hosts the create panel. */
  panel: ReactNode;
}

export function useFieldCreateAction({
  onCreated,
}: UseFieldCreateActionOptions): FieldCreateAction {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return {
    topAction: {
      label: (
        <span className="flex items-center gap-2">
          <Plus className="h-4 w-4" /> {t("PROFILE.FIELDS_CREATE")}
        </span>
      ),
      onSelect: () => setOpen(true),
    },
    panel: (
      <FieldEditPanel
        open={open}
        onClose={() => setOpen(false)}
        field={null}
        onSaved={(field) => {
          onCreated(field);
          setOpen(false);
        }}
        // Always elevated (not opt-in): stacks above a host Dialog/SidePanel via z-[75], which stays below the z-[80] dropdown portal so this panel's own dropdowns still layer on top.
        elevated
      />
    ),
  };
}
